import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface NavChild {
  label: string;
  to: string;
}

interface NavItem {
  label: string;
  to: string;
  children?: NavChild[];
}

const navItems: NavItem[] = [
  { label: "首页", to: "/" },
  {
    label: "动漫资讯",
    to: "/news",
    children: [
      { label: "最新资讯", to: "/news" },
      { label: "专题合集", to: "/special" },
    ],
  },
  {
    label: "作品推荐",
    to: "/recommend",
    children: [
      { label: "全部作品", to: "/recommend" },
      { label: "科幻机甲", to: "/recommend?category=%E7%A7%91%E5%B9%BB" },
      { label: "古风国漫", to: "/recommend?category=%E5%8F%A4%E9%A3%8E" },
    ],
  },
  { label: "专题", to: "/special" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileOpenGroup, setMobileOpenGroup] = useState<string | null>(null);

  useEffect(() => {
    setMobileOpen(false);
    setMobileOpenGroup(null);
  }, [location.pathname, location.search]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  const isActive = (item: NavItem) => {
    if (item.to === "/") return location.pathname === "/";
    return location.pathname.startsWith(item.to);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-background-200/80 bg-background-50/90 backdrop-blur-md">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2.5 transition-opacity hover:opacity-85">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 font-heading text-lg font-extrabold text-white">
              91
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-extrabold tracking-tight text-foreground-950">动漫</span>
              <span className="text-[10px] font-medium tracking-[0.18em] text-foreground-500">ANIME PORTAL</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((item) => (
              <div key={item.label} className="group relative">
                <Link
                  to={item.to}
                  className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                    isActive(item)
                      ? "text-primary-700"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {item.label}
                  {item.children && <i className="ri-arrow-down-s-line text-base" />}
                </Link>
                {item.children && (
                  <div className="invisible absolute left-0 top-full z-50 w-44 translate-y-1 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50 p-1.5 shadow-sm">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.to}
                          className="block rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form onSubmit={handleSearch} className="hidden items-center md:flex">
              <div className="flex items-center rounded-full border border-background-300/80 bg-background-100 px-3 py-1.5 transition-colors focus-within:border-accent-400">
                <i className="ri-search-line mr-2 text-base text-foreground-500" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索动漫资讯 / 作品"
                  className="w-40 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none lg:w-52"
                />
              </div>
            </form>

            <button
              type="button"
              onClick={() => navigate("/search")}
              aria-label="搜索"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
            >
              <i className="ri-search-line text-lg" />
            </button>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="菜单"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
            >
              <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-2-line text-xl"} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="max-h-[70vh] overflow-y-auto px-4 py-3">
            <form onSubmit={handleSearch} className="mb-3 flex items-center rounded-full border border-background-300/80 bg-background-100 px-3 py-2">
              <i className="ri-search-line mr-2 text-base text-foreground-500" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索动漫资讯 / 作品"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </form>
            <div className="flex flex-col">
              {navItems.map((item) => (
                <div key={item.label} className="border-b border-background-100 last:border-none">
                  <div className="flex items-center justify-between">
                    <Link
                      to={item.to}
                      className={`flex-1 py-3 text-sm font-medium ${
                        isActive(item) ? "text-primary-700" : "text-foreground-800"
                      }`}
                    >
                      {item.label}
                    </Link>
                    {item.children && (
                      <button
                        type="button"
                        aria-label="展开"
                        onClick={() =>
                          setMobileOpenGroup((prev) => (prev === item.label ? null : item.label))
                        }
                        className="flex h-9 w-9 cursor-pointer items-center justify-center text-foreground-500"
                      >
                        <i
                          className={
                            mobileOpenGroup === item.label
                              ? "ri-subtract-line text-lg"
                              : "ri-add-line text-lg"
                          }
                        />
                      </button>
                    )}
                  </div>
                  {item.children && mobileOpenGroup === item.label && (
                    <div className="flex flex-col pb-2 pl-3">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.to}
                          className="py-2 text-sm text-foreground-600"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}